import React from "react";
import { CardBody, CardHeader } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Breadcrumbs from "@src/@core/components/ui-components/breadcrumbs";
import Card from "@src/@core/components/ui-components/card";
import CustomAlerts from "@src/@core/components/ui-components/alerts";
import { LoaderOverlay } from "@src/@core/components/spinner";
import LoadingButton from "@components/ui-components/loading-button";
import AsyncSelectField from "@components/fields/react-select/async-select";
import useKpi from "./use-kpi";
import useFilters from "@src/views/common/hooks/use-filter";

const KPI = () => {
    const {
        data: { selectedKpis },
        loading,
        control,
        errors,
        selectedAttributes,
        handleRowActions
    } = useKpi();

    const { loadAsyncOptions } = useFilters();
    const { platformId } = useParams();
    const { t } = useTranslation();

    return (
        <>
            <Breadcrumbs items={[{ title: "KPI Attributes", active: true }]} />

            <CustomAlerts
                color="info"
                body="Select one or more attributes from the dropdown and click Add to configure them as KPIs."
                testId="kpi-info-alert"/>

            <LoaderOverlay
                active={loading}
                children={
                    <div className="row kpi-container">
                        <div className="col-md-6">
                            <Card className="mb-3">
                                <CardHeader className="m-0">
                                    <h4>{t("Add KPI Attributes")}</h4>
                                    <small className="text-muted">
                                        {t("Select attributes to add as KPIs")}
                                    </small>
                                </CardHeader>
                                <CardBody>
                                    <div className="mb-1">
                                        <AsyncSelectField
                                            loadOptions={(value, callback) => {
                                                loadAsyncOptions({
                                                    value,
                                                    callback: (options) => {
                                                        const formatted = options.map((opt) => {
                                                            const formatLabel = opt.label
                                                                .replace(/_/g, " ")
                                                                .replace(/\b\w/g, (char) => char.toUpperCase());
                                                            return { ...opt, label: formatLabel };
                                                        });
                                                        callback(formatted);
                                                    },
                                                    customURL: `/kpi-setting/${platformId}/attribute`
                                                });
                                            }}
                                            defaultOptions
                                            name="kpiAttributes"
                                            id="kpiAttributes"
                                            placeholder={t("Select Attributes")}
                                            control={control}
                                            errors={errors}
                                            isMulti
                                            isClearable
                                            testId="kpi-attribute-select"/>
                                    </div>
                                    <LoadingButton
                                        color="primary"
                                        text="Add KPIs"
                                        subject="settings"
                                        icon="plus"
                                        disabled={
                                            !selectedAttributes || selectedAttributes.length === 0
                                        }
                                        onClick={() => handleRowActions("add")}
                                        testId="add-kpi-btn"
                                        block/>
                                </CardBody>
                            </Card>

                            <Card
                                className="h-100">
                                <CardHeader>
                                    <h4>{t("Selected KPIs Attributes")}</h4>
                                    <small className="text-muted">
                                        {t("Preview of selected attributes")}
                                    </small>
                                </CardHeader>
                                <CardBody
                                    className="overflow-y-auto"
                                    style={{
                                        maxHeight: "calc(100vh - 450px)",
                                        minHeight: "150px"
                                    }}>
                                    {selectedAttributes && selectedAttributes.length > 0 ? (
                                        <div className="list-group">
                                            {selectedAttributes.map((attr, index) => (
                                                <div
                                                    key={index}
                                                    className="list-group-item d-flex align-items-center border rounded">
                                                    <span className="badge bg-light-secondary text-secondary me-2">
                                                        {index + 1}
                                                    </span>
                                                    <span className="fw-bold text-capitalize">
                                                        {attr.label}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-3">
                                            <p className="text-muted mb-0">
                                                {t("No attributes selected yet")}
                                            </p>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </div>

                        <div className="col-md-6 border-1">
                            <Card className="h-100">
                                <CardHeader>
                                    <h4>{t("Current KPIs")}</h4>
                                    <small className="text-muted">
                                        {t("List of all configured KPI")}
                                    </small>
                                </CardHeader>
                                <CardBody
                                    className="overflow-y-auto"
                                    style={{
                                        maxHeight: "calc(100vh - 250px)"
                                    }}>
                                    {selectedKpis && selectedKpis.length > 0 ? (
                                        <div className="list-group">
                                            {selectedKpis.map((kpi, index) => (
                                                <div
                                                    key={kpi._id}
                                                    className="list-group-item d-flex align-items-center justify-content-between border rounded">
                                                    <div className="d-flex align-items-center">
                                                        <span className="badge bg-light-primary text-primary me-2">
                                                            {index + 1}
                                                        </span>
                                                        <span className="fw-bold text-capitalize">
                                                            {kpi.name.replace(/_/g, " ")}
                                                        </span>
                                                    </div>

                                                    <LoadingButton
                                                        color="danger"
                                                        icon="delete"
                                                        testId="platform-delete"
                                                        subject="settings"
                                                        outline
                                                        className="btn-sm"
                                                        onClick={() => handleRowActions("delete", kpi)}/>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-4">
                                            <p className="text-muted">
                                                {t(
                                                    "No KPIs configured yet. Add some attributes above to get started."
                                                )}
                                            </p>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </div>
                    </div>
                }/>
        </>
    );
};

export default KPI;
